import React, { useState, useEffect } from 'react';
import { motion, AnimatePresence } from 'framer-motion';
import SafeIcon from '@/common/SafeIcon';
import { eventBus } from '@/utils/eventBus';

const BookingModal = () => {
  const [isOpen, setIsOpen] = useState(false);
  const [isSubmitting, setIsSubmitting] = useState(false);
  const [isSuccess, setIsSuccess] = useState(false);
  const [formData, setFormData] = useState({
    service: '',
    name: '',
    whatsapp: '',
    address: '',
    message: ''
  });

  useEffect(() => {
    const handleOpen = (data) => {
      setIsOpen(true);
      if (data && data.service) {
        setFormData(prev => ({ ...prev, service: data.service }));
      }
    };
    eventBus.on('openBookingModal', handleOpen);
    return () => eventBus.remove('openBookingModal', handleOpen);
  }, []);

  const handleChange = (e) => {
    const { name, value } = e.target;
    setFormData(prev => ({ ...prev, [name]: value }));
  };

  const handleSubmit = (e) => {
    e.preventDefault();
    setIsSubmitting(true);

    // Simulate network request
    setTimeout(() => {
      const bookings = JSON.parse(localStorage.getItem('sueti_bookings') || '[]');
      bookings.push({ ...formData, timestamp: new Date().toISOString() });
      localStorage.setItem('sueti_bookings', JSON.stringify(bookings));

      setIsSubmitting(false);
      setIsSuccess(true);

      setTimeout(() => {
        setIsOpen(false);
        setIsSuccess(false);
        setFormData({ service: '', name: '', whatsapp: '', address: '', message: '' });
      }, 3000);
    }, 1500);
  };

  return (
    <AnimatePresence>
      {isOpen && (
        <div className="fixed inset-0 z-[100] flex items-center justify-center p-4 sm:p-6 text-left">
          <motion.div
            initial={{ opacity: 0 }}
            animate={{ opacity: 1 }}
            exit={{ opacity: 0 }}
            onClick={() => !isSubmitting && !isSuccess && setIsOpen(false)}
            className="absolute inset-0 bg-slate-900/60 backdrop-blur-sm"
          />
          <motion.div
            initial={{ opacity: 0, scale: 0.95, y: 20 }}
            animate={{ opacity: 1, scale: 1, y: 0 }}
            exit={{ opacity: 0, scale: 0.95, y: 20 }}
            className="relative w-full max-w-lg bg-white shadow-2xl overflow-hidden border border-slate-100"
          >
            {/* Header */}
            <div className="px-8 pt-8 pb-4 text-center relative bg-white">
              <div className="flex items-center justify-center gap-3 mb-4">
                <div className="w-8 h-px bg-accent"></div>
                <span className="text-accent font-medium tracking-[0.2em] uppercase text-[10px]">Reservasi Eksklusif</span>
                <div className="w-8 h-px bg-accent"></div>
              </div>
              <h3 className="text-3xl font-serif text-slate-900 mb-4">Formulir Pemesanan</h3>

              <div className="bg-slate-50 border border-slate-100 p-4 rounded-sm mt-2">
                <p className="text-slate-600 text-xs leading-relaxed font-light italic">
                  Mohon maaf, saat ini semua agen kami sedang melayani pelanggan lain. Silakan isi detail perjalanan Anda di bawah ini dan kami akan memprioritaskan Anda sesegera mungkin.
                </p>
              </div>

              {!isSubmitting && !isSuccess && (
                <button
                  onClick={() => setIsOpen(false)}
                  className="absolute top-4 right-4 text-slate-400 hover:text-slate-800 transition-colors w-8 h-8 flex justify-center items-center rounded-sm hover:bg-slate-50"
                >
                  <SafeIcon name="X" className="w-5 h-5" />
                </button>
              )}
            </div>

            {/* Content */}
            <div className="px-8 pb-8">
              {isSuccess ? (
                <div className="flex flex-col items-center justify-center py-10 text-center">
                  <motion.div
                    initial={{ scale: 0 }}
                    animate={{ scale: 1 }}
                    transition={{ type: "spring", bounce: 0.5 }}
                    className="w-16 h-16 bg-green-100 text-green-600 rounded-full flex items-center justify-center mb-4"
                  >
                    <SafeIcon name="Check" className="text-3xl" />
                  </motion.div>
                  <h4 className="text-xl font-bold text-slate-800 mb-2">Pesan Terkirim!</h4>
                  <p className="text-slate-600 text-sm">
                    Terima kasih telah menghubungi Sueti Tour. Agen kami akan segera menindaklanjuti pesan Anda.
                  </p>
                </div>
              ) : (
                <form onSubmit={handleSubmit} className="space-y-5">
                  <div>
                    <label className="block text-[11px] font-medium uppercase tracking-wider text-slate-500 mb-2">Pilih Layanan</label>
                    <div className="relative">
                      <select
                        name="service"
                        required
                        value={formData.service}
                        onChange={handleChange}
                        className="w-full bg-slate-50/50 border border-slate-200 rounded-sm pl-4 pr-10 py-3 text-sm text-slate-800 focus:bg-white focus:ring-1 focus:ring-accent focus:border-accent outline-none transition-all duration-300 appearance-none"
                      >
                        <option value="" disabled>-- Tentukan Layanan Impian Anda --</option>
                        <option value="Wisata Dalam Negeri">Wisata Dalam Negeri</option>
                        <option value="Wisata Luar Negeri">Wisata Luar Negeri</option>
                        <option value="Wisata Haji & Umrah">Wisata Haji & Umrah</option>
                        <option value="Wisata Religi">Wisata Religi</option>
                        <option value="Lainnya">Lainnya / Konsultasi Umum</option>
                      </select>
                      <SafeIcon name="ChevronDown" className="w-4 h-4 text-slate-400 absolute right-4 top-1/2 -translate-y-1/2 pointer-events-none" />
                    </div>
                  </div>
                  <div>
                    <label className="block text-[11px] font-medium uppercase tracking-wider text-slate-500 mb-2">Nama Lengkap</label>
                    <input
                      type="text"
                      name="name"
                      required
                      value={formData.name}
                      onChange={handleChange}
                      placeholder="Masukkan nama Anda"
                      className="w-full bg-slate-50/50 border border-slate-200 rounded-sm px-4 py-3 text-sm text-slate-800 focus:bg-white focus:ring-1 focus:ring-accent focus:border-accent outline-none transition-all duration-300 placeholder:text-slate-400 font-light"
                    />
                  </div>
                  <div className="grid grid-cols-1 md:grid-cols-2 gap-5">
                    <div>
                      <label className="block text-[11px] font-medium uppercase tracking-wider text-slate-500 mb-2">Nomor WhatsApp</label>
                      <input
                        type="tel"
                        name="whatsapp"
                        required
                        value={formData.whatsapp}
                        onChange={handleChange}
                        placeholder="Contoh: 0812xxx"
                        className="w-full bg-slate-50/50 border border-slate-200 rounded-sm px-4 py-3 text-sm text-slate-800 focus:bg-white focus:ring-1 focus:ring-accent focus:border-accent outline-none transition-all duration-300 placeholder:text-slate-400 font-light"
                      />
                    </div>
                    <div>
                      <label className="block text-[11px] font-medium uppercase tracking-wider text-slate-500 mb-2">Kota Asal</label>
                      <input
                        type="text"
                        name="address"
                        required
                        value={formData.address}
                        onChange={handleChange}
                        placeholder="Jakarta / Bandung / dsj"
                        className="w-full bg-slate-50/50 border border-slate-200 rounded-sm px-4 py-3 text-sm text-slate-800 focus:bg-white focus:ring-1 focus:ring-accent focus:border-accent outline-none transition-all duration-300 placeholder:text-slate-400 font-light"
                      />
                    </div>
                  </div>


                  <button
                    type="submit"
                    disabled={isSubmitting}
                    className="w-full bg-slate-900 hover:bg-slate-800 text-white font-medium py-4 rounded-sm transition-all duration-300 flex justify-center items-center gap-3 mt-8 uppercase tracking-widest text-[11px] shadow-lg shadow-slate-900/10 disabled:opacity-70 disabled:cursor-not-allowed group"
                  >
                    {isSubmitting ? (
                      <>
                        <span className="w-4 h-4 border-2 border-white/30 border-t-white rounded-full animate-spin"></span>
                        Memproses Data...
                      </>
                    ) : (
                      <>
                        Kirim Pesan
                        <span className="group-hover:translate-x-1 transition-transform">→</span>
                      </>
                    )}
                  </button>
                </form>
              )}
            </div>
          </motion.div>
        </div>
      )}
    </AnimatePresence>
  );
};

export default BookingModal;
